import { getApplicationLanguageCode } from '@epi-store/application/application.selectors';
import * as ErrorDataHelper from '@epi-helpers/ErrorDataHelper';
import { getLoginState } from '@epi-selectors/login';
import { UnauthorizedError } from './UnauthorizedError';
import { ForbiddenError } from './ForbiddenError';
import { ApiError } from './ApiError';
import { reduxStore } from '../store-configuration';

type RequestInit = Parameters<typeof fetch>[1];

interface Headers {
  [key: string]: string;
}

export function createHeaders(extraHeaders: Headers): Headers {
  const state = reduxStore.getState();
  const languageCode = getApplicationLanguageCode(state);
  const loginState = getLoginState(state);

  const headers = {
    ...extraHeaders,
    Accept: '*/*',
    'Content-Type': 'application/json',
    'Accept-Language': languageCode
  };

  if (loginState?.accessToken) {
    return {
      ...headers,
      Authorization: `${loginState.tokenType} ${loginState.accessToken}`
    };
  }

  return headers;
}

function handleErrorResponse(
  response: Response,
  status: number,
  body: string,
  json: any
): Promise<never> {
  if (status === 401) {
    return Promise.reject(new UnauthorizedError(`messages.unauthorized_user`));
  }

  if (status === 422) {
    return Promise.reject(new ForbiddenError(`messages.incorrect_password`));
  }

  const errorMessage = `Failed status ${status} (${response.statusText}) on request ${response.url}.`;
  if (body) {
    return Promise.reject(new ApiError(errorMessage, json));
  }

  return Promise.reject(
    new Error(errorMessage, {
      cause: ErrorDataHelper.createErrorResponse([errorMessage])
    })
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function parseBody(responseBody: string): any {
  return responseBody ? JSON.parse(responseBody) : {};
}

function fetchRetry(
  url: string,
  options: RequestInit,
  n: number = 3,
  sleepMs: number = 2000
): Promise<[Response, number | null, string | null]> {
  const checkResultFunc = response => {
    if (response.ok || n <= 1) {
      return Promise.resolve([response, null, null]);
    }
    return response.text().then(responseBody => {
      const jsonResult = parseBody(responseBody);
      if (jsonResult.canRetry) {
        return sleep(sleepMs).then(() =>
          fetchRetry(url, options, n - 1, sleepMs)
        );
      }
      return Promise.resolve([response, response.status, responseBody]);
    });
  };
  return fetch(url, options).then(checkResultFunc);
}

export function checkResponse([response, resolvedStatus, resolvedBody]: [
  Response,
  number | null,
  string | null
]): Promise<any> {
  return Promise.all([
    resolvedStatus || response.status,
    response.bodyUsed ? resolvedBody || '' : response.text()
  ]).then(([status, body]) => {
    const json = parseBody(body);
    if (response.ok) {
      return Promise.resolve(json);
    }

    return handleErrorResponse(response, status, body, json);
  });
}

async function request<T>(
  url: string,
  method: string,
  json?: any,
  headers: Headers = {},
  abortController?: AbortSignal
): Promise<T> {
  return fetchRetry(url, {
    method,
    headers: createHeaders(headers),
    body: JSON.stringify(json),
    signal: abortController
  }).then(checkResponse);
}

async function postBase<T>(
  url: string,
  data: unknown,
  headers: Headers = {},
  abortController?: AbortSignal
): Promise<T> {
  return request(url, 'POST', data, headers, abortController);
}

export async function post<T>(url: string, data: unknown): Promise<T> {
  return new Promise((resolve, reject) => {
    const abortController = new AbortController();
    postBase(url, data, createHeaders({}), abortController.signal)
      .then(res => {
        if (res instanceof Promise) {
          res.then(result => resolve(result));
        } else {
          resolve(res as T);
        }
      })
      .catch(reject)
      .finally(() => abortController.abort());
  });
}

export function get<T>(
  url: string,
  options?: Record<string, any>,
  headers?: Headers,
  abortController?: AbortSignal
): Promise<T> {
  const u = new URL(url);
  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      u.searchParams.append(key, String(value));
    });
  }
  return request(
    u.toString(),
    'GET',
    undefined,
    createHeaders(headers || {}),
    abortController
  );
}

export function put<T>(
  url: string,
  json?: any,
  headers?: Headers,
  abortController?: AbortSignal
): Promise<T> {
  return request(
    url,
    'PUT',
    json,
    createHeaders(headers || {}),
    abortController
  );
}

export function patch<T>(
  url: string,
  json?: any,
  headers?: Headers,
  abortController?: AbortSignal
): Promise<T> {
  return request(
    url,
    'PATCH',
    json,
    createHeaders(headers || {}),
    abortController
  );
}

export function del<T>(
  url: string,
  json?: any,
  headers?: Headers,
  abortController?: AbortSignal
): Promise<T> {
  return request(
    url,
    'DELETE',
    json,
    createHeaders(headers || {}),
    abortController
  );
}

type SuccessCallback<T> = (result: T) => void;
type FailureCallback = (error: Error) => void;

export const fetchPost = <T>(
  url: string,
  data: unknown,
  onSuccess?: SuccessCallback<T>,
  onFailure?: FailureCallback,
  extraHeaders: Headers = {}
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const abortController = new AbortController();
    postBase(url, data, extraHeaders, abortController.signal)
      .then(
        res => {
          if (onSuccess) {
            return onSuccess(res as T);
          }
          resolve();
        },
        err => {
          if (onFailure) {
            onFailure(err);
          }
          reject(err);
        }
      )
      .catch(reject)
      .finally(() => abortController.abort());
  });
};

export const fetchPut = <T>(
  url: string,
  data: unknown,
  onSuccess?: SuccessCallback<T>,
  onFailure?: FailureCallback,
  extraHeaders: Headers = {}
): Promise<string | void> => {
  return new Promise((resolve, reject) => {
    const abortController = new AbortController();
    put(url, data, extraHeaders, abortController.signal)
      .then(
        res => {
          if (onSuccess) {
            onSuccess(res as T);
          }
          resolve('success');
        },
        err => {
          if (onFailure) {
            onFailure(err);
          }
          reject(err);
        }
      )
      .catch(err => {
        reject(err);
      })
      .finally(() => {
        abortController.abort();
      });
  });
};

export const fetchGet = <T>(
  url: string,
  options: Record<string, any> = {},
  onSuccess?: SuccessCallback<T>,
  onFailure?: FailureCallback,
  extraHeaders: Headers = {}
): Promise<string | void> => {
  return new Promise((resolve, reject) => {
    const abortController = new AbortController();
    get<T>(url, options, extraHeaders, abortController.signal)
      .then(res => {
        if (onSuccess) {
          return onSuccess(res);
        }
        resolve('success');
      })
      .catch(err => {
        if (onFailure) {
          onFailure(err);
        }
        reject(err);
      })
      .finally(() => abortController.abort());
  });
};

export const fetchDelete = <T>(
  url: string,
  data: unknown,
  onSuccess?: SuccessCallback<T>,
  onFailure?: FailureCallback,
  extraHeaders: Headers = {}
): Promise<string | void> => {
  return new Promise((resolve, reject) => {
    const abortController = new AbortController();
    del<T>(url, data, extraHeaders, abortController.signal)
      .then(res => {
        if (onSuccess) {
          return onSuccess(res);
        }
        resolve('success');
      })
      .catch(err => {
        if (onFailure) {
          onFailure(err);
        }
        reject(err);
      })
      .finally(() => abortController.abort());
  });
};

export default {
  checkResponse,
  post,
  get,
  put,
  patch,
  del,
  createHeaders
};
